import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, ButtonBase, Container, Divider, Grid, IconButton, Link as MuiLink, Typography } from '@mui/material';
import { yellow } from '@mui/material/colors';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { trackEvent } from './GoogleAnalytics';
import FeedbackDialog from './FeedbackDialog';
import BillConcernDialog from './BillConcernDialog';
import { useGeoLocation } from '../contexts/GeoLocationContext';
import { getFallbackData } from '../utils/billConfig';
import { navigationGroups } from '../utils/navigation';
import { stateNameToSlug } from '../utils/stateUtils';

const FooterLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => (
  <MuiLink
    component={Link}
    to={to}
    underline="hover"
    color="text.secondary"
    sx={{
      display: 'block',
      py: 1,
      minHeight: 44,
      boxSizing: 'border-box',
      fontSize: '0.875rem',
      '&:hover': { color: 'primary.main' },
    }}
  >
    {children}
  </MuiLink>
);

const FooterHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="h2"
    variant="overline"
    sx={{
      display: 'block',
      fontWeight: 700,
      mb: 1.5,
      letterSpacing: '0.08em',
      color: 'text.primary',
    }}
  >
    {children}
  </Typography>
);

const Footer: React.FC = () => {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [finderOpen, setFinderOpen] = useState(false);
  const { location } = useGeoLocation();
  const bills = getFallbackData();

  return (
    <Box
      component="footer"
      sx={{
        py: 5,
        px: 2,
        mt: 'auto',
        backgroundColor: 'transparent',
        borderTop: '1px solid',
        borderColor: 'divider',
        '& a:focus-visible, & button:focus-visible, & summary:focus-visible': {
          outline: '2px solid',
          outlineColor: 'text.primary',
          outlineOffset: 2,
        },
      }}
    >
      <Container maxWidth="lg">
        <Grid component="nav" aria-label="Footer navigation" container spacing={4}>
          {navigationGroups.map((group) => (
            <Grid item xs={12} sm={6} md={3} key={group.id}>
              <FooterHeading>{group.label}</FooterHeading>
              {group.id === 'bills' ? (
                <>
                  <MuiLink
                    component="button"
                    type="button"
                    underline="hover"
                    color="text.secondary"
                    aria-haspopup="dialog"
                    onClick={() => setFinderOpen(true)}
                    sx={{ display: 'block', py: 1, minHeight: 44, fontSize: '0.875rem', textAlign: 'left' }}
                  >
                    Find a bill for your concern
                  </MuiLink>
                  <FooterLink to="/family-court-reform-bills">Browse all bills</FooterLink>
                  <Box component="details">
                    <Box
                      component="summary"
                      sx={{
                        py: 1,
                        minHeight: 44,
                        boxSizing: 'border-box',
                        fontSize: '0.875rem',
                        color: 'text.secondary',
                        cursor: 'pointer',
                        '&:hover': { color: 'primary.main' },
                      }}
                    >
                      Browse bills by name ({bills.length})
                    </Box>
                    <Box sx={{ pl: 2, borderLeft: '1px solid', borderColor: 'divider' }}>
                      {bills.map((bill) => (
                        <FooterLink
                          key={bill.route}
                          to={
                            location?.state
                              ? `/bill/${stateNameToSlug(location.state)}/${bill.route}`
                              : `/bill/${bill.route}`
                          }
                        >
                          {bill.pageTitle}
                        </FooterLink>
                      ))}
                    </Box>
                  </Box>
                </>
              ) : (
                group.links.map((link) => (
                  <FooterLink key={link.to} to={link.to}>
                    {link.label}
                  </FooterLink>
                ))
              )}
            </Grid>
          ))}
          <Grid item xs={12} sm={6} md={3}>
            <FooterHeading>Articles</FooterHeading>
            <FooterLink to="/blog">Articles</FooterLink>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            RobertGarza.us © {new Date().getFullYear()}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MuiLink
              component="button"
              type="button"
              underline="hover"
              color="text.secondary"
              aria-haspopup="dialog"
              onClick={() => setFeedbackOpen(true)}
              sx={{ py: 1, minHeight: 44, fontSize: '0.875rem' }}
            >
              Give feedback
            </MuiLink>
            <IconButton
              component="a"
              href="https://www.facebook.com/robert.garza.5015"
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              aria-label="Facebook"
              onClick={() => trackEvent('footer_social_click', 'social', 'Facebook')}
            >
              <FacebookIcon />
            </IconButton>
            <IconButton
              component="a"
              href="https://www.instagram.com/robertgarza_texas/"
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              aria-label="Instagram"
              onClick={() => trackEvent('footer_social_click', 'social', 'Instagram')}
            >
              <InstagramIcon />
            </IconButton>
          </Box>
        </Box>
      </Container>
      <ButtonBase
        aria-haspopup="dialog"
        onClick={() => setFeedbackOpen(true)}
        sx={{
          display: 'none',
          '@media (min-width:768px)': { display: 'flex' },
          position: 'fixed',
          left: 0,
          top: '65%',
          transform: 'translateY(-50%)',
          zIndex: (theme) => theme.zIndex.appBar - 1,
          flexDirection: 'column',
          gap: 1,
          width: 44,
          py: 2,
          borderRadius: '0 8px 8px 0',
          bgcolor: yellow[500],
          color: 'common.black',
          fontSize: '0.875rem',
          fontWeight: 700,
          '&:hover': { bgcolor: yellow[600] },
          '&.Mui-focusVisible': { outline: '3px solid', outlineColor: 'text.primary', outlineOffset: 2 },
        }}
      >
        <ArrowForwardIcon fontSize="small" />
        <Box component="span" sx={{ writingMode: 'vertical-rl' }}>
          Feedback
        </Box>
      </ButtonBase>
      {finderOpen && <BillConcernDialog open={finderOpen} onClose={() => setFinderOpen(false)} />}
      {feedbackOpen && <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />}
    </Box>
  );
};

export default Footer;
